<script lang="ts">
	//Design system components
	import { Button, Input, Text, toast } from '@Shelly/shelly-design-system';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Types
	import type { Device } from '../../../../types/Device';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { DEVICES_UNDER_VOLTAGE_PROTECTION } from 'src/constants';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;
	const unit = 'V';

	let value = settings?.[get_gen_2_component_channel_structure(device)]?.undervoltage_limit;
	let cache = value;
	let min = 0;
	let max =
		DEVICES_UNDER_VOLTAGE_PROTECTION[device.type] !== undefined
			? DEVICES_UNDER_VOLTAGE_PROTECTION[device.type]
			: 279;
	let loading = false;
	const component = get_gen_2_component_from_category(device);

	$: error =
		+value < min || +value > max
			? $format('error_out_of_range', {
					default: `Value ${value} ${unit} is out of range. Please provide a value within the limits. (${min} ${unit} — ${max} ${unit})`,
					values: { value, max, min, unit },
				})
			: '';

	const sendRequest = async () => {
		if (+value < min || +value > max) {
			return toast.open({
				type: 'danger',
				title: $format('max_voltage_prot_limit_err_toast'),
			});
		}

		if (cache !== value) {
			loading = true;
			if (value === '') {
				value = 0;
			}
			const params = {
				id: device.channel,
				config: {
					undervoltage_limit: +value,
				},
			};
			const res = await gen2Request(device, `${component}.SetConfig`, params);
			const response = onSettingsResponse(res, device);
			if (response.isok) {
				settings[get_gen_2_component_channel_structure(device)].undervoltage_limit = value;
				devices_settings.update_devices_settings(id, settings);
				cache = value;
			} else {
				value = cache;
			}
			loading = false;
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: sendRequest, loading, disabled: loading || !!error });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="controls-small" class="mb-s">
	{$format('under_voltage_prot_description', {
		default: 'Under voltage protection in volts. Delete value and save to reset to default value.',
	})}
</Text>

<Input
	on:input={(e) => restrictDecimalNumberInput(e, 2)}
	bind:value
	size="m"
	type={'number'}
	{min}
	{max}
	{error}
	info={$format('maximum_voltage_value', {
		values: { max },
		default: 'Max: {max} V.',
	})}
	step={1}
	on:enter={sendRequest}
	id="input-mx9khka"
	aria-label={$format('under_voltage_protection', {
		default: 'Under voltage protection',
	})}
/>
{#if !$showNewLayout}
	<Button class="mt-s" {loading} disabled={loading} size="m" width="full" on:click={sendRequest}>
		{$format('button_save')}
	</Button>
{/if}
